/* eslint complexity: off */
import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import moment from 'moment';
import dig from 'object-dig';

import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import EditIcon from '@material-ui/icons/Edit';
import FaceIcon from '@material-ui/icons/Face';
import DeleteIcon from '@material-ui/icons/Delete';
import GetAppIcon from '@material-ui/icons/GetApp';
import RemoveRedEyeIcon from '@material-ui/icons/RemoveRedEye';
import EventIcon from '@material-ui/icons/Event';
import LocalHospitalIcon from '@material-ui/icons/LocalHospital';
import AssignmentIcon from '@material-ui/icons/Assignment';
import HealingIcon from '@material-ui/icons/Healing';
import SendIcon from '@material-ui/icons/Send';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import ReceiptIcon from '@material-ui/icons/Receipt';
import DescriptionIcon from '@material-ui/icons/Description';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import LocalPharmacyIcon from '@material-ui/icons/LocalPharmacy';

import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import InputLabel from '@material-ui/core/InputLabel';
import LinkMaterial from '@material-ui/core/Link';
import Chip from '@material-ui/core/Chip';
import Divider from '@material-ui/core/Divider';
import Box from '@material-ui/core/Box';
import LinearProgress from '~/components/LinearProgress';

import InputShow from '~/components/InputShow';
import GridContainer from '~/components/GridContainer';
import IconButton from '~/components/Button/IconButton';
import GridItem from '~/components/GridItem';
import Button from '~/components/Button';
import ModalDelete from '~/components/ModalDelete';

import Can from '~/contexts/Can';
import CurrentUser from '~/contexts/CurrentUser';

import Request from '~/providers/Request';
import isPresent from '~/utils/isPresent';
import { showPerson } from '~/config/routes';
import { URL } from '~/config/uri';

import New from './New';
import NewTriage from './NewTriage';
import EditTriage from './EditTriage';
import ShowImage from './ShowImage';
import MevoPatientConfirmModal from './MevoPatientConfirmModal';
import MevoPrescriptionModal from './MevoPrescriptionModal';

const sectionHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 8,
  marginTop: 16,
  color: '#1976d2',
};

const sectionTitleStyle = {
  fontWeight: 600,
  fontSize: 14,
  color: '#1976d2',
};

const fieldLabelStyle = {
  fontSize: 11,
  color: '#666',
  marginBottom: 2,
};

const fieldValueStyle = {
  fontSize: 14,
  color: '#333',
};

const emptyValueStyle = {
  fontSize: 13,
  color: '#999',
  fontStyle: 'italic',
};

const mevoStatusLabel = (s) =>
  ({
    iniciada: 'Iniciada',
    aguardando_integracao: 'Aguardando integração',
    finalizada: 'Finalizada',
    cancelada: 'Cancelada',
    expirada: 'Expirada',
    erro: 'Erro',
  }[s] || s || '-');

const mevoStatusColor = (s) =>
  ({
    iniciada: '#ef6c00',
    aguardando_integracao: '#1565c0',
    finalizada: '#2e7d32',
    cancelada: '#757575',
    expirada: '#757575',
    erro: '#c62828',
  }[s] || '#757575');

const mevoDocLabel = (tipo) =>
  ({
    RECEITA: 'Receita',
    EXAME: 'Solicitação de exame',
    ATESTADO: 'Atestado',
    ENCAMINHAMENTO: 'Encaminhamento',
    RELATORIO: 'Relatório',
    LME: 'LME',
    INSTRUCAO: 'Instrução',
    MANIPULADOS: 'Manipulados',
  }[tipo] || (tipo ? tipo.charAt(0) + tipo.slice(1).toLowerCase() : 'Documento'));

function SectionHeader({ icon: Icon, title }) {
  return (
    <Box style={sectionHeaderStyle}>
      <Icon style={{ fontSize: 18 }} />
      <Typography style={sectionTitleStyle}>{title}</Typography>
    </Box>
  );
}

function FieldDisplay({ label, value, html }) {
  const isEmpty = !value || value === '' || value === '-';

  return (
    <Box mb={1}>
      <Typography style={fieldLabelStyle}>{label}</Typography>
      {html && !isEmpty ? (
        <Typography
          style={fieldValueStyle}
          dangerouslySetInnerHTML={{ __html: value }}
        />
      ) : (
        <Typography style={isEmpty ? emptyValueStyle : fieldValueStyle}>
          {isEmpty ? 'Não informado' : value}
        </Typography>
      )}
    </Box>
  );
}

function MedicalRecordEvolution({ idClient, record }) {
  const { t } = useTranslation();
  const { user } = useContext(CurrentUser);
  const [loading, setLoading] = useState(false);

  const [isNew, setIsNew] = useState(false);
  const [isNewTriage, setIsNewTriage] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [evolution, setEvolution] = useState(null);

  const [evolutions, setEvolutions] = useState([]);
  const [reload, setReload] = useState(false);

  const [fileRemove, setFileRemove] = useState(null);
  const [loadingDelete, setLoadingDelete] = useState(false);

  const [evolutionRemove, setEvolutionRemove] = useState(null);
  const [loadingDeleteEvolution, setLoadingDeleteEvolution] = useState(false);

  const [isShowFile, setIsShowFile] = useState(false);
  const [fileShow, setFileShow] = useState(null);

  // Mevo (Receita Digital)
  const [mevoConfirm, setMevoConfirm] = useState(null); // evolução selecionada p/ confirmar
  const [mevoModal, setMevoModal] = useState(null); // { url, id }
  const [mevoPrescriptions, setMevoPrescriptions] = useState([]);
  const [reloadMevo, setReloadMevo] = useState(false);

  const mevoForEvolution = (evolutionId) =>
    mevoPrescriptions.filter(
      (p) => String(p.medical_record_evolution_id) === String(evolutionId)
    );

  async function resumeMevo(prescription) {
    try {
      const resp = await Request.post(
        `/admin/mevo/prescricoes/${prescription.id}/sessao`,
        {},
        {},
        { normalizeJSON: false }
      );
      if (resp && resp.modal_url) {
        setMevoModal({ url: resp.modal_url, id: prescription.id });
      }
    } catch (e) {
      console.error(e);
    }
  }

  async function downloadMevoDoc(fileUuid, name) {
    const authorizationToken = await Request.getAuth();
    fetch(`${URL}/admin/mevo/prescricoes/documentos/${fileUuid}`, {
      method: 'GET',
      headers: new Headers({ Authorization: `Bearer ${authorizationToken}` }),
    })
      .then((response) => response.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.setAttribute('href', url);
        a.setAttribute('download', name || 'documento.pdf');
        a.click();
      });
  }

  const copyMevoCode = (code) => {
    try {
      if (navigator.clipboard) navigator.clipboard.writeText(code);
      toast.success('Código copiado');
    } catch (e) {
      console.error(e);
    }
  };

  async function showFile(file) {
    setFileShow(file);
    setIsShowFile(true);
  }

  async function donwloadFile(file) {
    const authorizationToken = await Request.getAuth();
    fetch(`${URL}/admin/medical_record_evolutions/${file.uuid}/file`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${authorizationToken}`,
      }),
    })
      .then((response) => response.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.setAttribute('href', url);
        a.setAttribute('download', file.name);
        a.click();
      });
  }

  async function onDelete() {
    setLoadingDelete(true);

    Request.del(
      `/admin/medical_record_evolutions/${fileRemove.uuid}/file`,
      null,
      { normalizeJSON: false }
    )
      .then(() => {
        setFileRemove(null);
        setLoadingDelete(false);
        setReload(!reload);
        toast.success(t('delete-toast-success'));
      })
      .catch((e) => {
        console.error(e);
        setLoadingDelete(false);
      });
  }

  async function onDeleteEvolution() {
    setLoadingDeleteEvolution(true);

    Request.del(
      `/admin/medical_record_evolutions/${evolutionRemove.id}`,
      null,
      { normalizeJSON: false }
    )
      .then(() => {
        setEvolutionRemove(null);
        setLoadingDeleteEvolution(false);
        setReload(!reload);
        setReloadMevo((v) => !v);
        toast.success(t('delete-toast-success'));
      })
      .catch((e) => {
        console.error(e);
        setLoadingDeleteEvolution(false);
      });
  }

  // Helper to find included resource by type and id
  const findIncluded = (included, type, id) => {
    if (!included || !id) return null;
    return included.find(
      (item) => item.type === type && String(item.id) === String(id)
    );
  };

  useEffect(() => {
    async function getMedicalRecordEvolution() {
      setLoading(true);
      Request.get(`/admin/medical_record_evolutions/${idClient}`, {})
        .then((response) => {
          const included = response.included || [];

          // Process evolutions to resolve relationships
          const processedData = (response.data || [])
            .filter((row) => row.owner)
            .map((row) => {
              // Resolve referral_location - handle multiple formats
              let referralLocationName = null;

              // Case 1: referral_location is an embedded object with name
              if (row.referral_location && typeof row.referral_location === 'object') {
                referralLocationName = row.referral_location.name || row.referral_location.attributes?.name;
              }
              // Case 2: referral_location is a string
              else if (row.referral_location && typeof row.referral_location === 'string') {
                referralLocationName = row.referral_location;
              }

              // Case 3: JSON:API relationships/included pattern
              if (row.relationships?.referralLocation?.data) {
                const relData = row.relationships.referralLocation.data;
                const found = findIncluded(included, relData.type, relData.id);
                if (found) {
                  referralLocationName = found.attributes?.name || found.name;
                }
              }

              return {
                ...row,
                referral_location_name: referralLocationName,
              };
            });

          setLoading(false);
          setEvolutions(processedData);
        })
        .catch((e) => {
          console.error(e);
          setLoading(false);
        });
    }
    if (idClient && record.get('id')) {
      getMedicalRecordEvolution();
    }
  }, [idClient, record, reload]);

  useEffect(() => {
    async function getMevoPrescriptions() {
      if (!idClient || !user || user.role !== 'doctor') return;
      try {
        const response = await Request.get('/admin/mevo/prescricoes', {
          user_id: idClient,
        });
        setMevoPrescriptions(response.data || []);
      } catch (e) {
        console.error(e);
      }
    }
    getMevoPrescriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idClient, reloadMevo]);

  async function handleEvolution() {
    if (user && user.role === 'doctor') {
      setIsNew(true);
    } else if ((user && user.role === 'admin') || user.role === 'attendant') {
      setIsNewTriage(true);
    } else {
      toast.error('Você não tem permissão para adicionar uma evolução');
    }
  }

  const canShowNotes = (row) => {
    const userIsDoctor = user.role === 'doctor';
    const userHasCrp = isPresent(user.crp);
    const ownerIsDoctor = row.owner.role === 'doctor';
    const ownerHasCrp = isPresent(row.owner.crp);

    if (userIsDoctor && ownerIsDoctor && !ownerHasCrp) return true;
    if (userIsDoctor && userHasCrp && ownerIsDoctor && (user.crp === row.owner.crp || row.can_access) && ownerHasCrp) return true;
    if (row.owner.role !== 'doctor') return true;

    return false;
  };

  return (
    <>
      <LinearProgress loading={loading} />
      {record.get('id') && (
        <>
          <Can I="new" a="MedicalRecordEvolutions">
            <Typography color="primary" align="center">
              <Button color="green" onClick={handleEvolution}>
                Nova Evolução do Prontuário
              </Button>
            </Typography>
          </Can>

          {evolutions.map((row) => {
            const userIsDoctor = user.role === 'doctor';
            const ownerIsDoctor = row.owner.role === 'doctor';
            const showContent = canShowNotes(row);

            const exams = row.exams ? row.exams.join(', ') : '';
            const medication = row.medication ? row.medication.join(', ') : '';
            const hasActiveMevo = mevoForEvolution(row.id).some(
              (p) =>
                p.status === 'iniciada' ||
                p.status === 'aguardando_integracao' ||
                p.status === 'finalizada'
            );
            const cidMainText = row.cid_main
              ? `${row.cid_main.cid || ''} ${row.cid_main.description || ''}`.trim()
              : '';
            const cidSecondaryText = row.cid_secondary
              ? `${row.cid_secondary.cid || ''} ${row.cid_secondary.description || ''}`.trim()
              : '';
            const serviceExitText = row.service_exit
              ? row.service_exit.description || ''
              : '';
            // CID é diagnóstico (ato médico). Por boa prática (LGPD "mínimo
            // necessário" + acesso por perfil exigido pelo COFEN), o atendente
            // não visualiza o diagnóstico — apenas médico e admin. A Saída de
            // atendimento é dado operacional e permanece visível a todos.
            const canSeeCid = user.role !== 'attendant';
            const showCidMain = canSeeCid && !!cidMainText;
            const showCidSecondary = canSeeCid && !!cidSecondaryText;

            return (
              <Card key={row.id} style={{ margin: 10, borderRadius: 8 }}>
                <CardContent>
                  {/* HEADER */}
                  <Box
                    display="flex"
                    flexWrap="wrap"
                    alignItems="center"
                    style={{ gap: 12, marginBottom: 8 }}
                  >
                    {/* ID + Data */}
                    <Box
                      display="flex"
                      alignItems="center"
                      style={{ gap: 12, flexShrink: 0 }}
                    >
                      <Box
                        style={{
                          backgroundColor: '#e3f2fd',
                          borderRadius: 4,
                          padding: '4px 12px',
                          textAlign: 'center',
                          minWidth: 50,
                        }}
                      >
                        <Typography style={{ fontSize: 10, color: '#666' }}>
                          ID
                        </Typography>
                        <Typography style={{ fontWeight: 600, color: '#1976d2', fontSize: 14 }}>
                          {row.id}
                        </Typography>
                      </Box>

                      <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                        <EventIcon style={{ color: '#666', fontSize: 16 }} />
                        <Typography style={{ fontSize: 13, fontWeight: 500, color: '#333' }}>
                          {moment(row.date).format('DD/MM/YYYY [às] HH:mm')}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Owner Chip */}
                    <Box style={{ flex: 1, minWidth: 150 }}>
                      <Chip
                        icon={<FaceIcon />}
                        label={dig(row, 'owner', 'name')}
                        component={LinkMaterial}
                        href={showPerson.reverse({ id: dig(row, 'owner', 'id') })}
                        style={{ cursor: 'pointer' }}
                        target="_blank"
                        color="primary"
                        variant="outlined"
                        size="small"
                        onDelete={() => {}}
                        deleteIcon={<OpenInNewIcon style={{ fontSize: 14 }} />}
                      />
                    </Box>

                    {/* Edit + Delete */}
                    <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                      {!userIsDoctor && !ownerIsDoctor && (
                        <Can I="edit" a="MedicalRecordEvolutions">
                          {user.id === row.owner.id &&
                            moment().diff(moment(row.date), 'hours') <= 24 && (
                              <Button
                                color="orange"
                                size="small"
                                onClick={() => {
                                  setIsEdit(true);
                                  setEvolution(row);
                                }}
                                endIcon={<EditIcon />}
                              >
                                {t('action.edit')}
                              </Button>
                            )}
                        </Can>
                      )}
                      {user.role === 'admin' && (
                        <IconButton
                          tooltip="Excluir evolução"
                          Icon={DeleteIcon}
                          iconColor="red"
                          onClick={() => setEvolutionRemove(row)}
                        />
                      )}
                    </Box>
                  </Box>

                  <Divider style={{ margin: '12px 0' }} />

                  {/* EVOLUÇÃO */}
                  <SectionHeader icon={AssignmentIcon} title="Evolução" />
                  <Box style={{ backgroundColor: '#fafafa', padding: 12, borderRadius: 4 }}>
                    <Can I="show" a="MedicalRecordEvolutions">
                      {showContent ? (
                        <Typography
                          dangerouslySetInnerHTML={{ __html: row.notes }}
                          style={{ fontSize: 14 }}
                        />
                      ) : (
                        <Typography style={emptyValueStyle}>
                          {t('medical_record_evolutions.extras.not_permission_show')}
                        </Typography>
                      )}
                    </Can>
                    <Can not I="show" a="MedicalRecordEvolutions">
                      <Typography style={emptyValueStyle}>
                        {t('medical_record_evolutions.extras.not_permission_show')}
                      </Typography>
                    </Can>
                  </Box>

                  {/* INFORMAÇÕES GERAIS */}
                  {(showCidMain ||
                    showCidSecondary ||
                    serviceExitText ||
                    exams ||
                    row.physical_examination ||
                    row.conduct) && (
                    <>
                      <SectionHeader icon={LocalHospitalIcon} title="Informações Gerais" />
                      <GridContainer>
                        {showCidMain && (
                          <GridItem xs={12} sm={4}>
                            <FieldDisplay label="CID Principal" value={cidMainText} />
                          </GridItem>
                        )}
                        {showCidSecondary && (
                          <GridItem xs={12} sm={4}>
                            <FieldDisplay label="CID Secundário" value={cidSecondaryText} />
                          </GridItem>
                        )}
                        {serviceExitText && (
                          <GridItem xs={12} sm={4}>
                            <FieldDisplay
                              label="Saída de Atendimento"
                              value={serviceExitText}
                            />
                          </GridItem>
                        )}
                        {exams && (
                          <GridItem xs={12} sm={6}>
                            <FieldDisplay label="Exames Solicitados" value={exams} />
                          </GridItem>
                        )}
                        {row.physical_examination && (
                          <GridItem xs={12} sm={6}>
                            <FieldDisplay label="Exame Físico" value={row.physical_examination} />
                          </GridItem>
                        )}
                        {row.conduct && (
                          <GridItem xs={12}>
                            <FieldDisplay label="Conduta" value={row.conduct} />
                          </GridItem>
                        )}
                      </GridContainer>
                    </>
                  )}

                  {/* MEDICAÇÃO E POSOLOGIA */}
                  {(medication || row.posology || row.pills_quantity > 0) && (
                    <>
                      <SectionHeader icon={HealingIcon} title="Medicação e Posologia" />
                      <GridContainer>
                        {medication && (
                          <GridItem xs={12} sm={6}>
                            <FieldDisplay label="Medicamentos" value={medication} />
                          </GridItem>
                        )}
                        {row.pills_quantity > 0 && (
                          <GridItem xs={12} sm={2}>
                            <FieldDisplay label="Qtd. Comprimidos" value={String(row.pills_quantity)} />
                          </GridItem>
                        )}
                        {row.posology && (
                          <GridItem xs={12} sm={row.pills_quantity ? 4 : 6}>
                            <FieldDisplay label="Posologia / Instruções" value={row.posology} />
                          </GridItem>
                        )}
                      </GridContainer>
                    </>
                  )}

                  {/* ENCAMINHAMENTO */}
                  {(row.referral_specialty || row.referral_location_name || row.referral_notes) && (
                    <>
                      <SectionHeader icon={SendIcon} title="Encaminhamento" />
                      <GridContainer>
                        {row.referral_specialty && (
                          <GridItem xs={12} sm={4}>
                            <FieldDisplay label="Especialidade" value={row.referral_specialty} />
                          </GridItem>
                        )}
                        {row.referral_location_name && (
                          <GridItem xs={12} sm={4}>
                            <FieldDisplay
                              label="Local"
                              value={row.referral_location_name}
                            />
                          </GridItem>
                        )}
                        {row.referral_notes && (
                          <GridItem xs={12} sm={row.referral_location_name ? 4 : 8}>
                            <FieldDisplay label="Observações" value={row.referral_notes} />
                          </GridItem>
                        )}
                      </GridContainer>
                    </>
                  )}

                  {/* ANEXOS */}
                  {row.files && row.files.length > 0 && (
                    <>
                      <SectionHeader icon={AttachFileIcon} title="Anexos" />
                      <Box>
                        {row.files.map((item) => (
                          <Box
                            key={item.id}
                            display="flex"
                            alignItems="center"
                            style={{
                              backgroundColor: '#f5f5f5',
                              padding: '4px 8px',
                              borderRadius: 4,
                              marginBottom: 4,
                            }}
                          >
                            <IconButton
                              tooltip={t('action.show')}
                              onClick={() => showFile(item)}
                              Icon={RemoveRedEyeIcon}
                              iconColor="green"
                            />
                            <IconButton
                              tooltip={t('action.download')}
                              onClick={() => donwloadFile(item)}
                              Icon={GetAppIcon}
                              iconColor="blue"
                            />
                            {user.id === row.owner.id &&
                              moment().diff(moment(row.date), 'hours') <= 24 && (
                                <IconButton
                                  tooltip={t('action.remove')}
                                  onClick={() => setFileRemove(item)}
                                  Icon={DeleteIcon}
                                  iconColor="red"
                                />
                              )}
                            <Typography style={{ fontSize: 13, marginLeft: 8 }}>
                              {item.name}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </>
                  )}

                  {/* RECEITAS MEVO (já emitidas / em andamento) */}
                  {mevoForEvolution(row.id).length > 0 && (
                    <>
                      <SectionHeader icon={ReceiptIcon} title="Receitas Mevo" />
                      <Box>
                        {mevoForEvolution(row.id).map((p) => {
                          const statusColor = mevoStatusColor(p.status);
                          return (
                            <Box
                              key={p.id}
                              style={{
                                border: '1px solid #e0e0e0',
                                borderRadius: 8,
                                marginBottom: 10,
                                overflow: 'hidden',
                                backgroundColor: '#fff',
                              }}
                            >
                              {/* Cabeçalho: ícone + status + retomar */}
                              <Box
                                display="flex"
                                alignItems="center"
                                flexWrap="wrap"
                                style={{ gap: 8, padding: '8px 12px' }}
                              >
                                <LocalPharmacyIcon style={{ fontSize: 18, color: '#1976d2' }} />
                                <Box
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    padding: '2px 10px',
                                    borderRadius: 12,
                                    backgroundColor: `${statusColor}1A`,
                                  }}
                                >
                                  <span
                                    style={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: '50%',
                                      backgroundColor: statusColor,
                                    }}
                                  />
                                  <Typography
                                    style={{ fontSize: 12, fontWeight: 600, color: statusColor }}
                                  >
                                    {mevoStatusLabel(p.status)}
                                  </Typography>
                                </Box>
                                <Box style={{ flex: 1 }} />
                                {p.status === 'iniciada' && (
                                  <Button
                                    color="orange"
                                    size="small"
                                    onClick={() => resumeMevo(p)}
                                  >
                                    Retomar
                                  </Button>
                                )}
                              </Box>

                              {/* Código + data de emissão */}
                              {(p.codigo_validacao || p.finalized_at) && (
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  flexWrap="wrap"
                                  style={{ gap: 4, padding: '0 12px 8px' }}
                                >
                                  {p.codigo_validacao && (
                                    <Box
                                      display="inline-flex"
                                      alignItems="center"
                                      title="Clique para copiar"
                                      onClick={() => copyMevoCode(p.codigo_validacao)}
                                      style={{ gap: 4, cursor: 'pointer' }}
                                    >
                                      <Typography style={{ fontSize: 12, color: '#666' }}>
                                        Código{' '}
                                        <span
                                          style={{
                                            fontWeight: 600,
                                            color: '#333',
                                            fontFamily: 'monospace',
                                          }}
                                        >
                                          {p.codigo_validacao}
                                        </span>
                                      </Typography>
                                      <FileCopyIcon style={{ fontSize: 13, color: '#bbb' }} />
                                    </Box>
                                  )}
                                  {p.finalized_at && (
                                    <Typography style={{ fontSize: 12, color: '#999' }}>
                                      · emitida{' '}
                                      {moment(p.finalized_at).format('DD/MM/YYYY [às] HH:mm')}
                                    </Typography>
                                  )}
                                </Box>
                              )}

                              {/* Documentos */}
                              {p.documents && p.documents.length > 0 && (
                                <Box style={{ borderTop: '1px solid #eee' }}>
                                  {p.documents.map((d) => (
                                    <Box
                                      key={d.id}
                                      display="flex"
                                      alignItems="center"
                                      style={{
                                        gap: 8,
                                        padding: '6px 12px',
                                        borderBottom: '1px solid #f5f5f5',
                                      }}
                                    >
                                      <DescriptionIcon
                                        style={{ fontSize: 18, color: '#90a4ae' }}
                                      />
                                      <Typography style={{ fontSize: 13, color: '#333' }}>
                                        {mevoDocLabel(d.tipo_documento)}
                                      </Typography>
                                      {d.assinado && (
                                        <Box
                                          style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 3,
                                          }}
                                        >
                                          <CheckCircleIcon
                                            style={{ fontSize: 14, color: '#2e7d32' }}
                                          />
                                          <Typography
                                            style={{
                                              fontSize: 11,
                                              fontWeight: 600,
                                              color: '#2e7d32',
                                            }}
                                          >
                                            assinado
                                          </Typography>
                                        </Box>
                                      )}
                                      <Box style={{ flex: 1 }} />
                                      {d.file && d.file.uuid && (
                                        <Button
                                          color="primary"
                                          size="small"
                                          endIcon={<GetAppIcon />}
                                          onClick={() =>
                                            downloadMevoDoc(
                                              d.file.uuid,
                                              `${mevoDocLabel(d.tipo_documento)}.pdf`
                                            )
                                          }
                                        >
                                          Baixar
                                        </Button>
                                      )}
                                    </Box>
                                  ))}
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </>
                  )}

                  {/* EMITIR RECEITA DIGITAL (MEVO) — médico dono, até 24h, sem receita em andamento ou já emitida */}
                  {userIsDoctor &&
                    user.id === row.owner.id &&
                    moment().diff(moment(row.date), 'hours') <= 24 &&
                    !hasActiveMevo && (
                    <Box style={{ marginTop: 16 }}>
                      <Button
                        color="purple"
                        size="small"
                        endIcon={<ReceiptIcon />}
                        onClick={() => setMevoConfirm(row)}
                      >
                        Emitir Receita Digital (Mevo)
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </>
      )}

      <New
        isOpen={isNew}
        record={record}
        idClient={idClient}
        handleClose={() => setIsNew(false)}
        onSuccess={() => {
          setIsNew(false);
          setReload(!reload);
        }}
        onEmitMevo={(newEvolution) => {
          setIsNew(false);
          setReload(!reload);
          setMevoConfirm(newEvolution);
        }}
      />

      <NewTriage
        isOpen={isNewTriage}
        record={record}
        idClient={idClient}
        handleClose={() => setIsNewTriage(false)}
        onSuccess={() => {
          setIsNewTriage(false);
          setReload(!reload);
        }}
      />

      <EditTriage
        isOpen={isEdit}
        record={record}
        idClient={idClient}
        evolution={evolution}
        handleClose={() => {
          setEvolution(null);
          setIsEdit(false);
        }}
        onSuccess={() => {
          setEvolution(null);
          setIsEdit(false);
          setReload(!reload);
        }}
      />

      <ModalDelete
        modal={isPresent(fileRemove)}
        isLoading={loadingDelete}
        message={t('delete-modal-text')}
        buttonText={t('action.remove')}
        deleteAction={onDelete}
        onClose={() => setFileRemove(null)}
      />

      <ModalDelete
        modal={isPresent(evolutionRemove)}
        isLoading={loadingDeleteEvolution}
        message="Excluir esta evolução e as receitas Mevo vinculadas? Documentos já emitidos na Mevo continuam válidos para o paciente — isto remove apenas o registro aqui."
        buttonText={t('action.remove')}
        deleteAction={onDeleteEvolution}
        onClose={() => setEvolutionRemove(null)}
      />

      <ShowImage
        isOpen={isShowFile}
        file={fileShow}
        handleClose={() => {
          setFileShow(null);
          setIsShowFile(false);
        }}
      />

      <MevoPatientConfirmModal
        isOpen={isPresent(mevoConfirm)}
        record={record}
        idClient={idClient}
        evolution={mevoConfirm || {}}
        handleClose={() => setMevoConfirm(null)}
        onConfirmed={(prescription) => {
          setMevoConfirm(null);
          setMevoModal({ url: prescription.modal_url, id: prescription.id });
          setReloadMevo((v) => !v);
        }}
      />

      <MevoPrescriptionModal
        isOpen={isPresent(mevoModal)}
        modalUrl={mevoModal ? mevoModal.url : null}
        handleClose={() => {
          setMevoModal(null);
          setReloadMevo((v) => !v);
        }}
        onFinalized={async () => {
          const id = mevoModal && mevoModal.id;
          setMevoModal(null);
          // Médico finalizou no modal -> marca "aguardando integração" na hora
          // (não depende do webhook). O webhook depois traz os PDFs e o status
          // vira "finalizada".
          if (id) {
            try {
              await Request.post(
                `/admin/mevo/prescricoes/${id}/aguardando-integracao`,
                {},
                {},
                { normalizeJSON: false }
              );
            } catch (e) {
              console.error(e);
            }
          }
          // Poll curto da lista (o webhook é assíncrono).
          let tries = 0;
          const poll = setInterval(() => {
            tries += 1;
            setReloadMevo((v) => !v);
            if (tries >= 6) clearInterval(poll); // ~18s
          }, 3000);
        }}
        onExcluded={() => {
          setMevoModal(null);
          setReloadMevo((v) => !v);
        }}
      />

      <LinearProgress loading={loading} />
    </>
  );
}

export default React.memo(MedicalRecordEvolution);
